/**
 * notificationHelpers.js
 *
 * Utilities for writing notification documents and managing room subscriptions.
 *
 * Firestore collections used:
 *   notifications      – one doc per notification, keyed by recipient user_id
 *   roomSubscriptions  – tracks which users are subscribed to which forum rooms
 *
 * Suggested Firestore security rules (add to your rules file):
 *
 *   match /notifications/{id} {
 *     allow read, update: if request.auth.uid == resource.data.to_user_id;
 *     allow create: if request.auth != null;
 *   }
 *   match /roomSubscriptions/{id} {
 *     allow read, write: if request.auth != null;
 *   }
 */

import {
  collection, addDoc, getDocs, query, where, serverTimestamp,
  writeBatch, doc,
} from 'firebase/firestore';
import { db } from '../firebase';

// ─── Room subscriptions ───────────────────────────────────────────────────────

/**
 * Subscribe a user to a forum room (idempotent — safe to call on every post).
 * Called automatically when a user posts in a room for the first time.
 */
export async function subscribeToRoom(userId, room) {
  if (!userId || !room) return;
  try {
    const existing = await getDocs(
      query(
        collection(db, 'roomSubscriptions'),
        where('user_id', '==', userId),
        where('room', '==', room)
      )
    );
    if (existing.empty) {
      await addDoc(collection(db, 'roomSubscriptions'), { user_id: userId, room });
    }
  } catch (e) {
    console.error('subscribeToRoom error:', e);
  }
}

/**
 * Unsubscribe a user from a room (e.g. from a settings UI).
 * Not called automatically — user must opt out manually.
 */
export async function getMyRoomSubscriptions(userId) {
  if (!userId) return [];
  const snap = await getDocs(
    query(collection(db, 'roomSubscriptions'), where('user_id', '==', userId))
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ─── Broadcast to all users ───────────────────────────────────────────────────

/**
 * Write one notification doc for every user in the system, including the poster.
 * Fire-and-forget from the call site — do NOT await unless you need to block.
 */
export async function notifyAllUsers({ fromUserId, fromName, type, message, url }) {
  if (!fromUserId) return;
  try {
    const usersSnap = await getDocs(collection(db, 'users'));
    const userIds = usersSnap.docs.map(d => d.id);
    if (!userIds.length) return;

    const base = {
      from_user_id: fromUserId,
      from_name: fromName || 'Someone',
      type,
      message,
      url: url || '/dashboard',
      read: false,
      created_at: serverTimestamp(),
    };

    // Firestore batch limit is 500 ops; chunk if the user list is large
    const CHUNK = 499;
    for (let i = 0; i < userIds.length; i += CHUNK) {
      const batch = writeBatch(db);
      userIds.slice(i, i + CHUNK).forEach(uid => {
        const ref = doc(collection(db, 'notifications'));
        batch.set(ref, { ...base, to_user_id: uid });
      });
      await batch.commit();
    }
  } catch (e) {
    console.error('notifyAllUsers error:', e);
  }
}

// ─── Writing notifications ────────────────────────────────────────────────────

/**
 * Notify all subscribers of a discussion room about a new post.
 * Skips the poster themselves.
 */
export async function notifyRoomSubscribers({ room, fromUserId, fromName, message, url }) {
  if (!room || !fromUserId) return;
  try {
    const subsSnap = await getDocs(
      query(collection(db, 'roomSubscriptions'), where('room', '==', room))
    );

    const targets = subsSnap.docs
      .map(d => d.data().user_id)
      .filter(uid => uid !== fromUserId);

    if (!targets.length) return;

    await Promise.all(
      targets.map(uid =>
        addDoc(collection(db, 'notifications'), {
          to_user_id: uid,
          from_user_id: fromUserId,
          from_name: fromName || 'Someone',
          type: 'forum_post',
          room,
          message,
          url: url || '/theforum',
          read: false,
          created_at: serverTimestamp(),
        })
      )
    );
  } catch (e) {
    console.error('notifyRoomSubscribers error:', e);
  }
}

/**
 * Send a single notification to one user.
 * Silently skips if toUserId === fromUserId (no self-notifications).
 */
export async function notifyUser({ toUserId, fromUserId, fromName, type, message, url }) {
  if (!toUserId || !fromUserId || toUserId === fromUserId) return;
  try {
    await addDoc(collection(db, 'notifications'), {
      to_user_id: toUserId,
      from_user_id: fromUserId,
      from_name: fromName || 'Someone',
      type,
      message,
      url: url || '/dashboard',
      read: false,
      created_at: serverTimestamp(),
    });
  } catch (e) {
    console.error('notifyUser error:', e);
  }
}
